import {useAppDispatch, useAppSelector} from '@lib/store';
import {actions} from '@lib/store/src/data/timeslots/actions';
import {ReservationOwner, WorkspaceReservation} from '@lib/store/src/data/timeslots/apiTypes';
import {useEffect} from 'react';

export type Member = {
  reservation: WorkspaceReservation;
  owner: ReservationOwner;
  isOrganizer: boolean;
  isCurrentUser: boolean;
};
const LOADING = {status: 'pending'} as const;
const REJECTED = {status: 'rejected'} as const;
type ReservationDetails = {status: 'pending'} | {status: 'rejected'} | {status: 'fulfilled'; data: Array<Member>};

// This hook waits for all details to be loaded before exposing the data
// Basically assuming we'll get an endpoint that returns all of this in one go later.
// In the meantime, if you want a nice gradual load where you first show the reservations and later fill in
// owners, you can do so through custom selectors (after dispatching loadReservationDetails)
export const useReservationDetails = (workspaceReservation: WorkspaceReservation | null): ReservationDetails => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (workspaceReservation) {
      dispatch(actions.loadReservationDetails({workspaceReservation}));
    }
  }, [workspaceReservation]);

  const viewmodel = useAppSelector((state): ReservationDetails => {
    if (!workspaceReservation) return {data: [], status: 'fulfilled'};

    const members = Array<Member>();

    const parentId = workspaceReservation.hasInvitations ? workspaceReservation.id : workspaceReservation.invitationTo;

    // Solo
    const soloMember: Member = {
      reservation: workspaceReservation,
      isOrganizer: true,
      owner: {
        id: state.data.user.user.id,
        name: `${state.data.user.user.firstName} ${state.data.user.user.lastName}`,
        initials: `XY`, // TODO
        directoryObjectId: state.data.user.user.directoryObjectId,
        imageHash: state.data.user.user.imageHash ?? '',
        email: state.data.user.user.email,
      },
      isCurrentUser: true,
    };

    if (!parentId) {
      return {
        status: 'fulfilled' as const,
        data: [soloMember],
      };
    }

    // Get invitations
    const invitationsEntry = state.data.timeslots.childReservations[parentId];
    if (invitationsEntry?.status === 'rejected') {
      return REJECTED;
    }

    if (!invitationsEntry || invitationsEntry.status === 'pending') {
      return LOADING;
    }

    for (const invitation of invitationsEntry.data) {
      const ownerEntry = state.data.timeslots.owners[invitation.id];
      if (!ownerEntry || ownerEntry.status === 'pending') return LOADING;
      if (ownerEntry.status === 'rejected') return REJECTED;
      members.push({
        reservation: invitation,
        isOrganizer: false,
        owner: ownerEntry.data,
        isCurrentUser: invitation.ownerId === state.data.user.user.id,
      });
    }

    // Get parent
    if (parentId !== workspaceReservation.id) {
      const parentEntry = state.data.timeslots.parentReservations[parentId];
      if (parentEntry?.status === 'rejected') {
        return REJECTED;
      }

      if (!parentEntry || parentEntry?.status === 'pending') {
        return LOADING;
      }

      const ownerEntry = state.data.timeslots.owners[parentId];
      if (!ownerEntry || ownerEntry.status === 'pending') return LOADING;
      if (ownerEntry.status === 'rejected') return REJECTED;

      members.push({
        reservation: parentEntry.data,
        owner: ownerEntry.data,
        isOrganizer: true,
        isCurrentUser: parentEntry.data.ownerId === state.data.user.user.id,
      });
    } else {
      members.push(soloMember);
    }

    // Sort members
    members.sort((a, b) => {
      if (a.isOrganizer) return -1;
      if (b.isOrganizer) return 1;

      // Group different statuses
      const statusOrder = statusImportance[a.reservation.status] - statusImportance[b.reservation.status];
      if (statusOrder) return statusOrder;

      // Within status groups, sort alphabetically
      return a.owner.name.localeCompare(b.owner.name);
    });

    return {
      status: 'fulfilled' as const,
      data: members,
    };
  });

  return viewmodel;
};

const statusImportance: Record<WorkspaceReservation['status'], number> = {
  Confirmed: 0,
  InvitationPending: 1,
  InvitationDeclined: 2,
  Cancelled: 3,
  Denied: 3,
};
